import { useEffect, useState } from 'react';

import { api } from '../../services/api';
import type { Account, AccountLedgerResponse, AccountLedgerRow } from '../../services/types';
import { Button } from '../ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';

function formatMoney(value: number | null | undefined) {
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(Number(value || 0));
}

interface AccountLedgerDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    account: Pick<Account, 'id' | 'code' | 'name' | 'type'> | null;
    initialFrom?: string;
    initialTo?: string;
}

export function AccountLedgerDialog({
    open,
    onOpenChange,
    account,
    initialFrom = '',
    initialTo = '',
}: AccountLedgerDialogProps) {
    const [fromDate, setFromDate] = useState(initialFrom);
    const [toDate, setToDate] = useState(initialTo);
    const [ledger, setLedger] = useState<AccountLedgerResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [limit, setLimit] = useState(25);
    const [offset, setOffset] = useState(0);

    useEffect(() => {
        if (!open) return;
        setFromDate(initialFrom);
        setToDate(initialTo);
        setOffset(0);
    }, [initialFrom, initialTo, open, account?.id]);

    useEffect(() => {
        const accountId = account?.id;
        if (!open || typeof accountId !== 'number') {
            return;
        }
        const resolvedAccountId = accountId;

        let active = true;

        async function loadLedger() {
            setLoading(true);
            setError(null);
            try {
                const payload = await api.financials.getAccountLedger(resolvedAccountId, {
                    from: fromDate || undefined,
                    to: toDate || undefined,
                    limit,
                    offset,
                });
                if (active) {
                    setLedger(payload);
                }
            } catch (loadError: any) {
                if (active) {
                    setLedger(null);
                    setError(loadError?.message || 'Failed to load ledger details');
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        }

        void loadLedger();

        return () => {
            active = false;
        };
    }, [account?.id, fromDate, limit, offset, open, toDate]);

    const rows = ledger?.items || [];
    const total = ledger?.total || 0;
    const start = total === 0 ? 0 : offset + 1;
    const end = Math.min(offset + rows.length, total);
    const canGoBack = offset > 0;
    const canGoNext = offset + limit < total;
    const title = account ? `${account.code} · ${account.name}` : 'Account Ledger';

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="flex max-h-[92vh] w-[calc(100vw-2rem)] max-w-6xl flex-col overflow-hidden p-0"
                data-testid="account-ledger-dialog"
            >
                <DialogHeader className="shrink-0 border-b border-slate-200 px-6 py-5 pr-12">
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription>
                        {account?.type ? `${account.type} ledger drill-down` : 'Transaction-level ledger drill-down'}
                    </DialogDescription>
                </DialogHeader>

                <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden p-6">
                    <div className="shrink-0 flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 lg:flex-row lg:items-end lg:justify-between">
                        <div className="grid gap-3 sm:grid-cols-2">
                            <label className="space-y-1 text-sm">
                                <span className="font-medium text-slate-700">From</span>
                                <input
                                    type="date"
                                    className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm"
                                    value={fromDate}
                                    onChange={(event) => {
                                        setFromDate(event.target.value);
                                        setOffset(0);
                                    }}
                                />
                            </label>
                            <label className="space-y-1 text-sm">
                                <span className="font-medium text-slate-700">To</span>
                                <input
                                    type="date"
                                    className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm"
                                    value={toDate}
                                    onChange={(event) => {
                                        setToDate(event.target.value);
                                        setOffset(0);
                                    }}
                                />
                            </label>
                        </div>
                        <div className="text-sm text-slate-500">
                            {ledger?.basis || 'Journal-level breakdown for the selected account balance.'}
                        </div>
                    </div>

                    {error && (
                        <div className="shrink-0 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                            {error}
                        </div>
                    )}

                    <div
                        className="min-h-[260px] flex-1 overflow-auto rounded-xl border border-slate-200 bg-white"
                        data-testid="account-ledger-table-scroll"
                    >
                        <table className="w-full min-w-[980px] text-left text-sm">
                            <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 shadow-sm">
                                <tr>
                                    <th className="px-4 py-3 font-medium text-slate-500">Date</th>
                                    <th className="px-4 py-3 font-medium text-slate-500">Journal</th>
                                    <th className="px-4 py-3 font-medium text-slate-500">Source</th>
                                    <th className="px-4 py-3 font-medium text-slate-500">Description</th>
                                    <th className="px-4 py-3 font-medium text-slate-500">Reference</th>
                                    <th className="px-4 py-3 text-right font-medium text-slate-500">Debit</th>
                                    <th className="px-4 py-3 text-right font-medium text-slate-500">Credit</th>
                                    <th className="px-4 py-3 text-right font-medium text-slate-500">Running Balance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                                            Loading ledger details...
                                        </td>
                                    </tr>
                                ) : rows.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                                            No ledger entries match the current filters.
                                        </td>
                                    </tr>
                                ) : (
                                    rows.map((row: AccountLedgerRow, index) => (
                                        <tr key={row.row_id || row.line_id || `${row.journal_id}-${index}`} className="border-b border-slate-100 last:border-b-0">
                                            <td className="px-4 py-3 text-slate-700">{row.journal_date || '-'}</td>
                                            <td className="px-4 py-3 font-medium text-slate-900">{row.journal_no || `#${row.journal_id}`}</td>
                                            <td className="px-4 py-3 text-slate-600">{row.module_source || '-'}</td>
                                            <td className="px-4 py-3 text-slate-600">{row.description || '-'}</td>
                                            <td className="px-4 py-3 text-slate-600">{row.reference || '-'}</td>
                                            <td className="px-4 py-3 text-right tabular-nums text-slate-700">{formatMoney(row.debit)}</td>
                                            <td className="px-4 py-3 text-right tabular-nums text-slate-700">{formatMoney(row.credit)}</td>
                                            <td className="px-4 py-3 text-right font-semibold tabular-nums text-slate-900">{formatMoney(row.running_balance)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="shrink-0 flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-3">
                            <span className="font-medium text-slate-800">
                                {start}-{end} of {total}
                            </span>
                            <label className="flex items-center gap-2">
                                <span className="text-xs uppercase tracking-wide text-slate-500">Page size</span>
                                <select
                                    className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
                                    value={String(limit)}
                                    onChange={(event) => {
                                        setLimit(Number(event.target.value));
                                        setOffset(0);
                                    }}
                                >
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                            </label>
                        </div>

                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => setOffset(Math.max(0, offset - limit))} disabled={!canGoBack || loading}>
                                Previous
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => setOffset(offset + limit)} disabled={!canGoNext || loading}>
                                Next
                            </Button>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
